/**
 * eliteTopicSequence — the ONE source of truth for "where am I" in Elite
 * Mastery (27-Aug-2026).
 *
 * Hierarchy:  Technology → Section → Topic (one markdown document)
 *             → "## N. Title" headings inside the topic ("On this page")
 *
 * The learner-facing sequence of a technology is derived — never stored —
 * from the technology's published `mapping.json` (written by the Content
 * Factory in roadmap order, and the same dataset the billing page catalog is
 * synced from), grouped by section in order of first appearance, topics in
 * mapping order within their section. That is exactly the order the left
 * navigation shows, so the sidebar, the "Topic 7 of 31" label, Previous /
 * Next and the progress figures can never disagree.
 *
 * Everything here is pure and synchronous: build once per technology load
 * (O(n), fine for 500+ topics), then look positions up by file in O(1).
 *
 * Terminology: the product already calls a document a "Topic" (sidebar,
 * "Previous Topic" / "Next Topic" titles, "Current Topic" card) — used as is.
 */

export const TOPIC_TERM = 'Topic';
export const TOPIC_QUERY_PARAM = 'topic';
export const TECH_QUERY_PARAM = 'tech';

/** mapping.json key: strip non-alphanumerics, lowercase (generateMapping.js's slug()). */
export const topicKeyFromPath = (relativePath) =>
  String(relativePath || '')
    .split('/')
    .pop()
    .replace(/\.md$/i, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

/**
 * Turn one technology's mapping.json into the tree the sidebar renders AND
 * the ordered sequence the navigation uses. Sections keep first-appearance
 * order; topics keep mapping order within a section.
 *
 * @param {{ id: string, title?: string, docsFolder?: string }} tech   catalog entry
 * @param {Record<string,string>} mapping   { key: "Section/Topic.md" }
 */
export const buildTechnologyTree = (tech, mapping) => {
  const docsFolder = tech.docsFolder || tech.id;
  const sections = new Map();
  Object.entries(mapping || {}).forEach(([key, rawPath]) => {
    if (typeof rawPath !== 'string' || !rawPath.trim()) return;
    const path = rawPath.replace(/^\/+/, '');
    const parts = path.split('/');
    const sectionTitle = (parts.length > 1 ? parts[0] : 'General').replace(/_/g, ' ');
    const title = parts[parts.length - 1].replace(/\.md$/i, '').replace(/_/g, ' ');
    if (!sections.has(sectionTitle)) sections.set(sectionTitle, { topic: sectionTitle, subtopics: [] });
    sections.get(sectionTitle).subtopics.push({ title, file: `/docs/${docsFolder}/${path}`, key: String(key), path });
  });
  return { technology: tech.id, title: tech.title || tech.id, docsFolder, topics: [...sections.values()] };
};

/**
 * Flatten one technology tree into the canonical sequence.
 * Returns { entries, byFile, byKey, total, sections } where every entry is
 *   { index, position, total, file, key, title, techId, techTitle,
 *     sectionTitle, sectionIndex, sectionCount, indexInSection, sectionTotal }
 */
export const buildTopicSequence = (techTree) => {
  const entries = [];
  const byFile = new Map();
  const byKey = new Map();
  const sections = [];
  if (techTree && Array.isArray(techTree.topics)) {
    techTree.topics.forEach((section, sectionIndex) => {
      const subtopics = Array.isArray(section.subtopics) ? section.subtopics : [];
      sections.push({ title: section.topic, index: sectionIndex, total: subtopics.length, firstIndex: entries.length });
      subtopics.forEach((sub, indexInSection) => {
        const entry = {
          index: entries.length,
          position: entries.length + 1,
          total: 0, // filled below
          file: sub.file,
          key: sub.key || topicKeyFromPath(sub.file),
          title: sub.title,
          techId: techTree.technology,
          techTitle: techTree.title || techTree.technology,
          sectionTitle: section.topic,
          sectionIndex,
          sectionCount: techTree.topics.length,
          indexInSection,
          sectionTotal: subtopics.length,
        };
        entries.push(entry);
        byFile.set(entry.file, entry);
        byFile.set(entry.file.replace(/^\//, ''), entry); // callers sometimes drop the leading slash
        if (!byKey.has(entry.key)) byKey.set(entry.key, entry);
      });
    });
  }
  entries.forEach((e) => { e.total = entries.length; });
  return { techId: techTree ? techTree.technology : null, entries, byFile, byKey, total: entries.length, sections };
};

/** Sequences for every loaded technology, keyed by technology id. No cross-technology sequence exists. */
export const buildSequences = (technologies) => {
  const map = new Map();
  (technologies || []).forEach((t) => { if (t && t.technology) map.set(t.technology, buildTopicSequence(t)); });
  return map;
};

/** Find the sequence + entry that owns a file across all loaded technologies. */
export const locateFile = (sequences, file) => {
  if (!file) return null;
  for (const seq of sequences.values()) {
    const entry = seq.byFile.get(file) || seq.byFile.get(String(file).replace(/^\//, ''));
    if (entry) return { sequence: seq, entry };
  }
  return null;
};

/** The sidebar's "college / tenant user" check (full legacy access). */
export const isLegacyCollegeUser = () => {
  try {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const isTenantHost = !!host && host !== 'theskillgenie.com' && host !== 'www.theskillgenie.com' && host !== 'localhost' && host !== '127.0.0.1';
    const user = JSON.parse((typeof localStorage !== 'undefined' && localStorage.getItem('user')) || '{}');
    return !!((user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || isTenantHost));
  } catch (_) {
    return false;
  }
};

/**
 * Legacy (non-Nexus) access rule — the sidebar's, unchanged:
 * paid / college users see everything; otherwise "Level 1-5" documents are
 * free and, failing a level marker, the first two documents of a technology.
 */
export const legacyIsLocked = (file, indexInTechnology, hasFullAccess) => {
  if (hasFullAccess) return false;
  const match = String(file || '').match(/level[- ]?(\d+)/i);
  if (match) {
    const level = parseInt(match[1], 10);
    return !(level >= 1 && level <= 5);
  }
  return indexInTechnology >= 2;
};

/**
 * Resolve the navigation model for the current file.
 *
 * @param {ReturnType<typeof buildTopicSequence>|null} sequence
 * @param {string|null} currentFile
 * @param {(entry) => { locked: boolean, state?: string }} accessFor   existing entitlement rule (Nexus map or legacy)
 * @returns {null | {
 *   current, previous, next, position, total, isFirst, isLast,
 *   section: { title, index, count, position, total }, technology: { id, title },
 *   previousCrossesSection, nextCrossesSection, previousAccess, nextAccess,
 * }}
 */
export const resolveNavigation = (sequence, currentFile, accessFor = () => ({ locked: false })) => {
  if (!sequence || !currentFile) return null;
  const current = sequence.byFile.get(currentFile) || sequence.byFile.get(String(currentFile).replace(/^\//, ''));
  if (!current) return null;
  const previous = current.index > 0 ? sequence.entries[current.index - 1] : null;
  const next = current.index < sequence.total - 1 ? sequence.entries[current.index + 1] : null;
  const access = (e) => (e ? (accessFor(e) || { locked: false }) : null);
  return {
    current,
    previous,
    next,
    position: current.position,
    total: sequence.total,
    isFirst: !previous,
    isLast: !next,
    section: { title: current.sectionTitle, index: current.sectionIndex, count: current.sectionCount, position: current.indexInSection + 1, total: current.sectionTotal },
    technology: { id: current.techId, title: current.techTitle },
    previousCrossesSection: !!previous && previous.sectionIndex !== current.sectionIndex,
    nextCrossesSection: !!next && next.sectionIndex !== current.sectionIndex,
    previousAccess: access(previous),
    nextAccess: access(next),
  };
};

/** "Topic 7 of 31" — never "Topic 7 of undefined". */
export const positionLabel = (nav, term = TOPIC_TERM) => {
  if (!nav || !Number.isFinite(nav.position) || !Number.isFinite(nav.total) || nav.total < 1) return '';
  return `${term} ${nav.position} of ${nav.total}`;
};

/** Query string for a topic URL — the existing `?tech=` convention plus the mapping key. */
export const topicSearch = (techId, key, extra = null) => {
  const params = new URLSearchParams(extra || '');
  if (techId) params.set(TECH_QUERY_PARAM, techId); else params.delete(TECH_QUERY_PARAM);
  if (key) params.set(TOPIC_QUERY_PARAM, key); else params.delete(TOPIC_QUERY_PARAM);
  const s = params.toString();
  return s ? `?${s}` : '';
};

export const parseTopicSearch = (search) => {
  const params = new URLSearchParams(search || '');
  return { tech: params.get(TECH_QUERY_PARAM) || null, topic: params.get(TOPIC_QUERY_PARAM) || null, file: params.get('file') || null };
};

/** Completed topics of a technology, from the existing localStorage completion store (`completed_docs_<tech>`). */
export const completedFiles = (techId) => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(`completed_docs_${techId}`) : null;
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list : []);
  } catch (_) {
    return new Set();
  }
};

const eliteTopicSequence = {
  TOPIC_TERM, buildTechnologyTree, buildTopicSequence, buildSequences, locateFile, legacyIsLocked, isLegacyCollegeUser, resolveNavigation, positionLabel,
  topicSearch, parseTopicSearch, topicKeyFromPath, completedFiles,
};
export default eliteTopicSequence;
